import { Styles, Text, View } from '@react-pdf/renderer';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import {
  Content,
  getContent,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKETPLACE_SALE_PAYMENT_STATE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { getFormattedEventName } from 'src/utils/eventWithDataUtils';
import { getFormattedLocation } from 'src/utils/format';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import {
  Event,
  ListingDetails,
  Performer,
  SaleDetails,
  Seating,
  Venue,
} from 'src/WebApiController';

import { getSeatDisplayText } from '../common/SeatingInfo';
import { styles } from './SaleInvoicePDF.css';
import {
  SaleInvoiceAddress,
  SaleInvoicePayments,
} from './SaleInvoicePDF.types';
import { formatDate } from './SaleInvoicePDF.utils';

export const InvoiceLabeledField = ({
  labelContentId,
  value,
  styleOverride,
}: {
  labelContentId?: ContentId;
  value: React.ReactNode;
  styleOverride?: Styles;
}) => {
  const sanitizedStyle = useMemo(() => {
    if (styleOverride && 'container' in styleOverride) {
      return [styles.labelContainer, styleOverride['container']];
    }
    return styles.labelContainer;
  }, [styleOverride]);
  return (
    <View style={sanitizedStyle}>
      <View style={styles.label}>
        <Text>
          <Content id={labelContentId} />
        </Text>
      </View>

      <View style={styles.value}>
        <Text>{value}</Text>
      </View>
    </View>
  );
};

export const InvoiceLabeledSection = ({
  labelContentId,
  children,
}: {
  labelContentId?: ContentId;
  children: React.ReactNode;
}) => {
  return (
    <View style={[styles.labelContainer, { fontSize: 12 }]}>
      <Text style={styles.label}>
        <Content id={labelContentId} />
      </Text>
      <View style={styles.value}>{children}</View>
    </View>
  );
};

export const InvoiceAddress = ({
  invoiceAddress,
}: {
  invoiceAddress: SaleInvoiceAddress;
}) => {
  const { name, address, city, state, zip, country, phoneNumber } =
    invoiceAddress;
  return (
    <View style={styles.addressContainer}>
      <Text>{name}</Text>
      <Text>{address}</Text>
      <Text>{`${city}, ${state} ${zip}`}</Text>
      <Text>{country}</Text>
      <Text>{phoneNumber}</Text>
    </View>
  );
};

export const InvoicePayment = ({
  paymentTerms,
  paymentType,
  deliveryMethod,
  email,
}: {
  paymentTerms?: string;
  paymentType?: string;
  deliveryMethod?: string;
  email?: string;
}) => {
  const naText = useContent(ContentId.NA);
  return (
    <View style={styles.paymentContainer}>
      <InvoiceLabeledField
        labelContentId={ContentId.PaymentTerms}
        value={paymentTerms ?? naText}
        styleOverride={{ container: { fontSize: 11 } }}
      />
      <InvoiceLabeledField
        labelContentId={ContentId.PaymentType}
        value={paymentType ?? naText}
        styleOverride={{ container: { fontSize: 11 } }}
      />
      <InvoiceLabeledField
        labelContentId={ContentId.DeliveryType}
        value={deliveryMethod ?? naText}
        styleOverride={{ container: { fontSize: 11 } }}
      />
      <InvoiceLabeledField
        labelContentId={ContentId.Email}
        value={email ?? naText}
        styleOverride={{ container: { fontSize: 11 } }}
      />
    </View>
  );
};

export const InvoiceLineItemEvent = ({
  event,
  performer,
  venue,
}: {
  event: Event;
  performer?: Performer;
  venue?: Venue;
}) => {
  const [formattedEventName] = getFormattedEventName({
    event,
    performer,
    venue,
    useShortName: true,
  });
  const location = venue ? getFormattedLocation(venue) : undefined;

  return (
    <View style={styles.cellContentStack}>
      <Text>{formattedEventName}</Text>
      {venue?.name && <Text>{venue.name}</Text>}
      {location && <Text>{location}</Text>}
    </View>
  );
};

export const InvoiceSeating = ({ seating }: { seating: Seating }) => {
  const naText = useContent(ContentId.NA);
  const { section, row, seatFr, seatTo } = seating;
  return (
    <View style={styles.cellContentStack}>
      <Text>
        <Content id={ContentId.Section} />: {section ?? naText}
      </Text>
      <Text>
        <Content id={ContentId.Row} />: {row ?? naText}
      </Text>
      <Text>
        <Content id={ContentId.Seat} />:{' '}
        {getSeatDisplayText({ seatFr, seatTo })}
      </Text>
    </View>
  );
};

export const InvoiceLineTable = ({
  event,
  performer,
  venue,
  sale,
}: {
  event: Event;
  performer?: Performer;
  venue?: Venue;
  sale: SaleDetails;
}) => {
  const ticketUnitCost = useMemo(() => {
    const unitCostAmt =
      sale.ticketCnt === 0 ? 0 : (sale.soldTktCost?.amt ?? 0) / sale.ticketCnt;
    return {
      ...sale.soldTktCost,
      amt: unitCostAmt,
    };
  }, [sale.soldTktCost, sale.ticketCnt]);
  if (sale.ticketGroupInfos == null || sale.ticketGroupInfos.length === 0) {
    return null;
  }
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>
        <Content id={ContentId.InvoiceLines} />
      </Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableCell}>
            <Content id={ContentId.Date}></Content>
          </Text>
          <Text style={[styles.tableCell, { flex: 2 }]}>
            <Content id={ContentId.Event} />
          </Text>
          <Text style={styles.tableCell}>
            <Content id={ContentId.Seating} />
          </Text>
          <Text style={[styles.tableCell, styles.textRightAlign]}>
            <Content id={ContentId.QuantityAbbreviated} />
          </Text>
          <Text style={[styles.tableCell, styles.textRightAlign]}>
            <Content id={ContentId.UnitCost} />
          </Text>
          <Text style={[styles.tableCell, styles.textRightAlign]}>
            <Content id={ContentId.Amount} />
          </Text>
        </View>
        {sale.ticketGroupInfos.map((tg, index) => (
          <View key={index} style={styles.tableRow}>
            <Text style={styles.tableCell}>
              {formatDate(event.dates.start, true, true)}
            </Text>
            <View style={[styles.tableCell, { flex: 2 }]}>
              <InvoiceLineItemEvent
                event={event}
                performer={performer}
                venue={venue}
              />
            </View>
            <View style={styles.tableCell}>
              <InvoiceSeating seating={tg.seating} />
            </View>
            <Text style={[styles.tableCell, styles.textRightAlign]}>
              {tg.ticketCount?.toString() || '-'}
            </Text>
            <Text style={[styles.tableCell, styles.textRightAlign]}>
              {applyNumberFormatting({
                inputNumber: ticketUnitCost.amt,
                currencyCode: ticketUnitCost.currency,
              })}
            </Text>
            <Text style={[styles.tableCell, styles.textRightAlign]}>
              {applyNumberFormatting({
                inputNumber: ticketUnitCost.amt * (tg.ticketCount ?? 0),
                currencyCode: ticketUnitCost.currency,
              })}
            </Text>
          </View>
        ))}
      </View>
      <View style={styles.summary}>
        <Text>
          <Content id={ContentId.InventorySubtotal} />: {sale.soldTktCost?.disp}
        </Text>
      </View>
    </View>
  );
};

export const CostsAndCredits = ({
  sale,
  payments,
  saleListing,
}: {
  sale: SaleDetails;
  payments: SaleInvoicePayments;
  saleListing?: ListingDetails | null;
}) => {
  const contentContext = useContentContext();
  const paymentState = useMemo(() => {
    const naText = getContent(ContentId.NA, contentContext);
    if (!sale.mkpPmtState) {
      return naText;
    }
    const stateContentId =
      MARKETPLACE_SALE_PAYMENT_STATE_TO_CID[sale.mkpPmtState];
    return stateContentId ? getContent(stateContentId, contentContext) : naText;
  }, [contentContext, sale.mkpPmtState]);

  const seatTraits = useMemo(
    () => saleListing?.seatTraits ?? [],
    [saleListing?.seatTraits]
  );

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>
        <Content id={ContentId.OtherCostsAndCredits} />:
      </Text>

      <View style={[styles.twoColumnRow, styles.textBold]}>
        <Text>
          <Content id={ContentId.Description}></Content>
        </Text>
        <Text>
          <Content id={ContentId.Amount} />
        </Text>
      </View>
      {sale.credits && (
        <View style={styles.twoColumnRow}>
          <Text>
            <Content id={ContentId.Credits}></Content>
          </Text>
          <Text>{sale.credits.disp}</Text>
        </View>
      )}
      {sale.charges && (
        <View style={styles.twoColumnRow}>
          <Text>
            <Content id={ContentId.Charges}></Content>
          </Text>
          <Text>{`(${sale.charges.disp})`}</Text>
        </View>
      )}
      <View style={styles.summary}>
        <Text>
          <Content id={ContentId.CostAndCreditsSubtotal} />:{' '}
          {payments.costAndCredit}
        </Text>
      </View>
      <View style={styles.twoColumnRow}>
        <InvoiceLabeledSection labelContentId={ContentId.Notes}>
          {!isEmpty(seatTraits) && (
            <View style={styles.cellContentStack}>
              {seatTraits.map((trait) => (
                <Text key={trait.listingNoteId}>{trait.listingNoteText}</Text>
              ))}
            </View>
          )}
        </InvoiceLabeledSection>
        <View style={styles.rightAlignedColumns}>
          <Text style={styles.textRightAlign}>
            <Content id={ContentId.MarkedAs} />: {paymentState}
          </Text>
        </View>
      </View>
    </View>
  );
};
